const esc = (v) =>
  String(v ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')

const money = (n) =>
  `৳${Number(n || 0).toLocaleString('en-BD', { maximumFractionDigits: 2 })}`

export const buildInvoiceHtml = (order) => {
  const isPreOrder = !!order?.isPreOrder
  const advancePercent = order?.advancePercent || 10
  const fullAmount = Number(order?.fullAmount || order?.payableAmount || 0)
  const payableAmount = Number(order?.payableAmount || 0)
  const dueAmount = Number(
    order?.dueAmount ?? Math.max(0, fullAmount - payableAmount)
  )
  const dueCollected = Number(order?.dueCollectedAmount || 0)
  const dueRemaining = Math.max(0, dueAmount - dueCollected)
  const dateStr = new Date(order?.createdAt || Date.now()).toLocaleString(
    'en-GB',
    {
      day: 'numeric',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }
  )
  const status = String(order?.paymentStatus || 'pending').toUpperCase()
  const statusClass =
    order?.paymentStatus === 'paid'
      ? 'ok'
      : order?.paymentStatus === 'pending'
        ? 'warn'
        : 'bad'

  return `<!DOCTYPE html>
<html>
<head>
  <meta charset="utf-8" />
  <title>Invoice ${esc(order?.tranId)}</title>
  <style>
    * { box-sizing: border-box; margin: 0; padding: 0; }
    body {
      font-family: Arial, Helvetica, sans-serif;
      color: #111827;
      background: #fff;
      padding: 28px;
      font-size: 13px;
      line-height: 1.45;
    }
    .sheet { max-width: 800px; margin: 0 auto; }
    .top {
      display: flex;
      justify-content: space-between;
      gap: 24px;
      padding-bottom: 16px;
      border-bottom: 3px solid #ea2229;
      margin-bottom: 18px;
    }
    .brand { color: #ea2229; font-size: 22px; font-weight: 800; margin-bottom: 6px; }
    .muted { color: #6b7280; font-size: 12px; line-height: 1.5; }
    .right { text-align: right; }
    .title { font-size: 26px; font-weight: 800; letter-spacing: 1px; margin-bottom: 8px; }
    .meta div { margin-bottom: 3px; }
    .badge {
      display: inline-block;
      margin-top: 8px;
      padding: 3px 10px;
      border-radius: 999px;
      font-size: 11px;
      font-weight: 700;
    }
    .badge.ok { background: #dcfce7; color: #166534; }
    .badge.warn { background: #fef3c7; color: #92400e; }
    .badge.bad { background: #fee2e2; color: #991b1b; }
    .grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: 16px;
      margin-bottom: 18px;
    }
    .box {
      background: #f9fafb;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      padding: 12px 14px;
    }
    .box h4 {
      font-size: 11px;
      text-transform: uppercase;
      letter-spacing: 0.06em;
      color: #9ca3af;
      margin-bottom: 8px;
    }
    .box strong { display: block; font-size: 14px; margin-bottom: 4px; }
    table {
      width: 100%;
      border-collapse: collapse;
      margin-bottom: 16px;
    }
    th {
      text-align: left;
      background: #111827;
      color: #fff;
      padding: 10px 12px;
      font-size: 12px;
    }
    th:last-child, td:last-child { text-align: right; }
    td {
      padding: 12px;
      border-bottom: 1px solid #e5e7eb;
      vertical-align: top;
    }
    .item-title { font-weight: 700; margin-bottom: 2px; }
    .item-sub { color: #6b7280; font-size: 12px; }
    .totals {
      width: 300px;
      margin-left: auto;
      border: 1px solid #e5e7eb;
      border-radius: 8px;
      overflow: hidden;
    }
    .totals .row {
      display: flex;
      justify-content: space-between;
      padding: 8px 12px;
      border-bottom: 1px solid #f3f4f6;
      color: #4b5563;
    }
    .totals .row:last-child { border-bottom: 0; }
    .totals .grand {
      background: #111827;
      color: #fff;
      font-weight: 700;
      font-size: 14px;
    }
    .accent { color: #ea2229; font-weight: 700; }
    .footer {
      margin-top: 18px;
      padding-top: 12px;
      border-top: 1px solid #e5e7eb;
      color: #6b7280;
      font-size: 12px;
    }
    @media print {
      body { padding: 12px; }
      @page { margin: 12mm; size: A4; }
    }
  </style>
</head>
<body>
  <div class="sheet">
    <div class="top">
      <div>
        <div class="brand">Eyevary Limited</div>
        <div class="muted">
          (Temporary): House 458, Road 6, Avenue 5, DOHS Mirpur, Dhaka, Bangladesh<br/>
          Phone: +8801848080808 · Email: store@eyevary.com<br/>
          Web: www.eyevary.com
        </div>
      </div>
      <div class="right">
        <div class="title">INVOICE</div>
        <div class="meta muted">
          <div><strong>Invoice No:</strong> ${esc(order?.tranId)}</div>
          <div><strong>Date:</strong> ${esc(dateStr)}</div>
          <div><strong>Type:</strong> ${
            isPreOrder
              ? `Pre-Order (${advancePercent}% advance)`
              : 'Standard purchase'
          }</div>
        </div>
        <span class="badge ${statusClass}">${esc(status)}</span>
      </div>
    </div>

    <div class="grid">
      <div class="box">
        <h4>Bill to</h4>
        <strong>${esc(order?.buyer?.name)}</strong>
        <div class="muted">
          Phone: ${esc(order?.buyer?.phone)}<br/>
          Email: ${esc(order?.buyer?.email)}<br/>
          Address: ${esc(order?.buyer?.address)}
        </div>
      </div>
      <div class="box">
        <h4>Payment summary</h4>
        <div class="muted">
          Method: ${isPreOrder ? 'SSLCommerz (advance)' : 'SSLCommerz'}<br/>
          Product warranty: ${esc(order?.product?.warrantyPeriod || 'N/A')}<br/>
          Qty: ${esc(order?.qty || 1)}
          ${
            order?.dueStatus && order.dueStatus !== 'n/a'
              ? `<br/>Due status: ${esc(String(order.dueStatus).toUpperCase())}`
              : ''
          }
        </div>
      </div>
    </div>

    <table>
      <thead>
        <tr>
          <th>Item</th>
          <th>Qty</th>
          <th>Unit</th>
          <th>Total</th>
        </tr>
      </thead>
      <tbody>
        <tr>
          <td>
            <div class="item-title">${esc(order?.product?.title)}</div>
            ${
              order?.product?.warrantyPeriod
                ? `<div class="item-sub">Warranty: ${esc(
                    order.product.warrantyPeriod
                  )}</div>`
                : ''
            }
            ${
              order?.discount
                ? `<div class="item-sub">Discount: ${esc(
                    order.discount
                  )}%</div>`
                : ''
            }
          </td>
          <td>${esc(order?.qty || 1)}</td>
          <td>${money(order?.unitPrice || fullAmount)}</td>
          <td>${money(fullAmount)}</td>
        </tr>
      </tbody>
    </table>

    <div class="totals">
      <div class="row"><span>Subtotal</span><span>${money(fullAmount)}</span></div>
      ${
        isPreOrder
          ? `<div class="row"><span>Advance paid (${advancePercent}%)</span><span class="accent">${money(
              payableAmount
            )}</span></div>
             <div class="row"><span>Due amount</span><span>${money(
               dueAmount
             )}</span></div>
             <div class="row"><span>Due collected</span><span>${money(
               dueCollected
             )}</span></div>
             <div class="row"><span>Due remaining</span><span>${money(
               dueRemaining
             )}</span></div>`
          : ''
      }
      <div class="row grand"><span>Amount paid online</span><span>${money(
        payableAmount || fullAmount
      )}</span></div>
    </div>

    <div class="footer">
      ${
        isPreOrder
          ? `This is a pre-order invoice. Advance of ${money(
              payableAmount
            )} (${advancePercent}%) was paid online. Remaining due ${money(
              dueRemaining
            )} is payable later / on delivery.`
          : 'Thank you for shopping with Eyevary Limited. For support: store@eyevary.com · +8801848080808'
      }
    </div>
  </div>
</body>
</html>`
}

/** Print via hidden iframe — no popup window required. */
export const printInvoice = (order) => {
  if (!order || typeof document === 'undefined') return

  const html = buildInvoiceHtml(order)
  const iframeId = 'eyevary-invoice-print-frame'
  let iframe = document.getElementById(iframeId)

  if (!iframe) {
    iframe = document.createElement('iframe')
    iframe.id = iframeId
    iframe.setAttribute('title', 'Invoice print')
    iframe.style.position = 'fixed'
    iframe.style.right = '0'
    iframe.style.bottom = '0'
    iframe.style.width = '0'
    iframe.style.height = '0'
    iframe.style.border = '0'
    iframe.style.opacity = '0'
    iframe.style.pointerEvents = 'none'
    document.body.appendChild(iframe)
  }

  const frameWindow = iframe.contentWindow
  const frameDoc = frameWindow?.document
  if (!frameDoc || !frameWindow) return

  frameDoc.open()
  frameDoc.write(html)
  frameDoc.close()

  const triggerPrint = () => {
    try {
      frameWindow.focus()
      frameWindow.print()
    } catch (e) {
      console.log('Invoice print failed', e)
    }
  }

  if (frameDoc.readyState === 'complete') {
    setTimeout(triggerPrint, 150)
  } else {
    iframe.onload = () => setTimeout(triggerPrint, 150)
  }
}
